/* eslint-disable @typescript-eslint/no-this-alias */
import { busEvent, EventBusEnum, InterceptorLocalAuth, isLocalMode } from '@tencent/s2-client-vue-lib';
import ELEMENT, { Message } from 'element-ui';
import Vue from 'vue';
import i18n from '@/lang';
import { ElLoadingComponent } from 'element-ui/types/loading';
import moment from 'moment';
import { LibsThrottler } from './libs/LibsThrottler';
import NProgress from 'nprogress';
import { appModule, IForbiddenData } from './store/modules/ModuleApp';
import { IUserData, userModule } from './store/modules/ModuleUser';
import { isoToDateStrFormat, isoToDateTimeStrFormat } from './libs/LibsDateUtils';

/**
 * 全局事件监听
 *
 * @export
 * @class AppDecorater
 */
export class AppDecorater {
	/** 节流阀 */
	private throttler: LibsThrottler;

	/** constructor */
	constructor() {
		this.init();
		this.throttler = new LibsThrottler();
	}

	/**
	 * 注册事件
	 *
	 * @private
	 *
	 * @memberOf AppDecorater
	 */
	public init(): void {
		busEvent.$on(EventBusEnum.APPS_OPEN_LOADING, this.openLoading.bind(this));
		busEvent.$on(EventBusEnum.APPS_CLOSE_LOADING, this.closeLoading.bind(this));
		busEvent.$on(EventBusEnum.APPS_SHOW_ERROR, this.showError.bind(this));
		busEvent.$on(EventBusEnum.APPS_SHOW_ERROR_DETAIL, this.showErrorWithDetail.bind(this));
		busEvent.$on(EventBusEnum.APPS_SET_MESSAGER, this.setAppMessager.bind(this));
		// busEvent.$on(EventBusEnum.APPS_FORBIDDEN_CHANGE, this.onForbiddenChange.bind(this));
		busEvent.$on(EventBusEnum.APPS_FORBIDDEN_CHANGE_DETAIL, this.onForbiddenChange.bind(this));
	}

	/**
	 * 打开LOADING
	 *
	 * @private
	 * @param {*} request
	 * @returns {*}
	 *
	 * @memberOf AppDecorater
	 */
	private openLoading(request: any): any {
		NProgress.start();
	}

	/**
	 * 关闭LOADING
	 *
	 * @private
	 * @param {*} loading
	 * @returns {*}
	 *
	 * @memberOf AppDecorater
	 */
	private closeLoading(loading: any): any {
		NProgress.done();
	}

	/**
	 * 接收403更新
	 *
	 * @private
	 * @param {*} message
	 *
	 * @memberOf AppDecorater
	 */
	private onForbiddenChange(message: any): void {
		const forbiddenChangeData: IForbiddenData = { ...appModule.forbiddenChange, error: message };
		forbiddenChangeData.isForbidden = true;
		appModule.forbiddenChangeUpdate(forbiddenChangeData);
	}

	/**
	 * 提示错误
	 *
	 * @private
	 * @param {*} message
	 * @returns {*}
	 *
	 * @memberOf AppDecorater
	 */
	private async showError(message: any): Promise<any> {
		// Message: 401 Unauthorized!
		console.log('showError..', message);

		// 本地开发时决定是不是需要跳转
		if (isLocalMode() && message === 'Message: 401 Unauthorized!') {
			await new InterceptorLocalAuth().interceptorServerAuth();
		}

		this.throttler.throttle(async () => {
			if (window.location.href.indexOf('#/home/index') === -1) {
				this.displayErrorMessage(message);
				AppDocker.getInstance().pipeMessage('error', message);
			}
		}, 500);
	}

	/** 显示错误信息 */
	private displayErrorMessage(message: any): void {
		const userData: IUserData = userModule.userData;
		if (userData && userData.id) {
			// console.log('输出错误提示', message);
			ELEMENT.Message.error(message);
		}
	}

	/**
	 * 错误详情
	 *
	 * @private
	 * @param {*} message
	 * @param {*} details
	 * @returns {*}
	 *
	 * @memberOf AppDecorater
	 */
	private showErrorWithDetail({ message, details }: any): any {
		let messageVal: any = `${message}<div style="text-align:left">`;

		details.forEach((detailVal: any, index: number) => {
			// eslint-disable-next-line no-control-regex
			let msg: any = detailVal.message.replace(new RegExp('\r\n', 'g'), '<br>');

			// eslint-disable-next-line no-control-regex
			msg = msg.replace(new RegExp('\n', 'g'), '<br>');
			messageVal += `${index + 1}.${msg}<br>`;
		});
		messageVal += '</div>';

		ELEMENT.MessageBox.alert(`${messageVal}`, '提示', {
			dangerouslyUseHTMLString: true,
			customClass: 'detail-message-box'
		});
	}

	/**
	 * 设置信息收集对象
	 *
	 * @private
	 * @param {*} messager
	 *
	 * @memberOf AppDecorater
	 */
	private setAppMessager(messager: any): void {
		busEvent.$off('apps-set-messager', this.setAppMessager.bind(this));
		AppDocker.getInstance().setPipeMessager(messager);
	}
}

// =================================================================================================================================
/** 价格过滤器 */
export const priceFilter: any = (num: number, precision = 2): any => {
	if (num && num !== 0) {
		const val: string = Number(num).toFixed(precision);
		const thousand: Array<string> = val.toString().split('.');
		thousand[0] = thousand[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

		return thousand.join('.');
	}

	return `0.${'0'.repeat(precision)}`;
};

/**
 * 小数四舍五入
 * @param val 传入要处理的数据
 * @param fractionDigits 要保留的位数
 */
export function workWithNumberFilter(val: number, fractionDigits = 2): string {
	if (val && val !== 0) {
		const m = Number((Math.abs(val) * 10 ** fractionDigits).toPrecision(15));
		const num = (Math.round(m) / 10 ** fractionDigits) * Math.sign(val);
		// console.log('传入的数据：', val, '保留的位数：', fractionDigits, 'return出来的数据：', num);

		return priceFilter(num, fractionDigits);
	}

	return `0.${'0'.repeat(fractionDigits)}`;
}

/** 数字过滤器 */
export const numberFilter: any = (num: number): any => {
	// eslint-disable-next-line eqeqeq
	if (num && num != 0) {
		const thousand = num.toString().split('.');
		thousand[0] = thousand[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

		return thousand.join('.');
	}

	return 0;
};

/** 邮箱后缀过滤器 */
export const emailAppendFilter: any = (val: string, append: string = '@tencent.com'): any => {
	if (val && val.trim()) {
		return `${val}${append}`;
	}

	return val;
};

/** 带翻译的格式化时间 */
export const formatTimeWithLocale: any = (time: any): any => {
	if (!time) {
		return time;
	}

	if (typeof time === 'string') {
		const temp = time.split(' ');

		if (temp.length > 1) {
			return `${isoToDateStrFormat(time)} ${temp[1]}`;
		}

		return isoToDateStrFormat(time);
	}

	// return moment(time).format(formatKey);
	return isoToDateTimeStrFormat(time);
};
/** 转换时间格式 */
export const formatTime: any = (time: number = 0): string => {
	if (time) {
		// const timeOffset = new Date().getTimezoneOffset();
		const convertedTime = time.toString().length === 10 ? Number(time) * 1000 : Number(time) * 1000;

		return moment(convertedTime).format('YYYY-MM-DD');
	}

	return '';
};

/** 转换时间格式——精准到秒 */
export const formatSecondTime: any = (time: number = 0): string => {
	if (time) {
		// const timeOffset = new Date().getTimezoneOffset();
		const convertedTime = time.toString().length === 10 ? Number(time) * 1000 : Number(time);

		return moment(convertedTime).format('YYYY-MM-DD HH:mm:ss');
	}

	return '';
};

// =================================================================================================================================

/**
 * 将一个浮点数转成整数，返回整数和倍数。如 3.14 >> 314，倍数是 100
 *
 * @param numberValue
 * @returns {{times: number, num: number}}
 */
const toInteger: any = (numberValue: any): any => {
	const ret: any = {
		times: 1,
		num: 0
	};
	const isInteger: any = Math.floor(numberValue) === numberValue;

	if (isInteger) {
		ret.num = numberValue;

		return ret;
	}
	// eslint-disable-next-line padding-line-between-statements
	const strfi: any = String(numberValue);
	const dotPos: any = strfi.indexOf('.');
	const len: any = strfi.substr(dotPos + 1).length;

	// eslint-disable-next-line no-restricted-properties
	const times: any = Math.pow(10, len);
	const intNum: any = parseInt((numberValue * times + 0.5).toString(), 10);

	ret.times = times;
	ret.num = intNum;

	return ret;
};

/**
 * 实现加减乘除运算，确保不丢失精度
 * 把小数放大为整数（乘），进行算术运算，再缩小为小数（除）
 *
 * @param number1
 * @param number2
 * @param action
 * @returns {number}
 */
export const operation: any = (number1: any, number2: any, action: any): any => {
	const o1: any = toInteger(number1);
	const o2: any = toInteger(number2);
	const n1: any = o1.num;
	const n2: any = o2.num;
	const t1: any = o1.times;
	const t2: any = o2.times;
	const max: any = t1 > t2 ? t1 : t2;
	let result: any = null;

	switch (action) {
		case 'add':
			if (t1 === t2) {
				// 两个小数位数相同

				result = n1 + n2;
			} else if (t1 > t2) {
				// o1 小数位 大于 o2

				result = n1 + n2 * (t1 / t2);
			} else {
				// o1 小数位 小于 o2

				result = n1 * (t2 / t1) + n2;
			}

			return result / max;
		case 'subtract':
			if (t1 === t2) {
				result = n1 - n2;
			} else if (t1 > t2) {
				result = n1 - n2 * (t1 / t2);
			} else {
				result = n1 * (t2 / t1) - n2;
			}

			return result / max;
		case 'multiply':
			// eslint-disable-next-line no-extra-parens
			result = (n1 * n2) / (t1 * t2);

			return result;
		case 'divide':
			// eslint-disable-next-line no-extra-parens
			result = (n1 / n2) * (t2 / t1);

			return result;
		// no default
	}
};

/**
 * 加法运算
 *
 * @param number1
 * @param number2
 * @returns {number}
 */
export const add: any = (number1: any, number2: any): any => operation(number1, number2, 'add');

/**
 * 减法运算
 *
 * @param number1
 * @param number2
 * @returns {number}
 */
export const subtract: any = (number1: any, number2: any): any => operation(number1, number2, 'subtract');

/**
 * 乘法运算
 *
 * @param number1
 * @param number2
 * @returns {number}
 */
export const multiply: any = (number1: any, number2: any): any => operation(number1, number2, 'multiply');

/**
 * 除法运算
 *
 * @param number1
 * @param number2
 * @returns {number}
 */
export const divide: any = (number1: any, number2: any): any => operation(number1, number2, 'divide');

const showMessage = Symbol('showMessage');

/**
 * 重写 elementui message
 *
 */
export class AppCustomMessager {
	/** success */
	public success(options: any, single = true): void {
		this[showMessage]('success', options, single);
	}

	/** warning */
	public warning(options: any, single = true): void {
		this[showMessage]('warning', options, single);
	}

	/** info */
	public info(options: any, single = true): void {
		this[showMessage]('info', options, single);
	}

	/** error */
	public error(options: any, single = true): void {
		// console.log('error invoke..', options);
		this[showMessage]('error', options, single);
	}

	/** 弹提示重写 */
	public [showMessage](type: any, options: any, single: any): void {
		if (options) {
			if (single) {
				if (document.getElementsByClassName('el-message').length === 0) {
					const message: any = Message;
					message[type](options);
					AppDocker.getInstance().pipeMessage(type, options);
				}
			} else {
				const message: any = Message;
				message[type](options);
				AppDocker.getInstance().pipeMessage(type, options);
			}
		}
	}
}

/** 全局常量对象 */
export class AppDocker {
	/**
	 * 配置服务单例
	 *
	 * @static
	 * @returns {AppDocker}
	 */
	public static getInstance(): AppDocker {
		if (!AppDocker.instance) {
			AppDocker.instance = new AppDocker();
		}

		return AppDocker.instance;
	}

	private static instance: AppDocker;

	/**
	 * 错误信息收集器
	 *
	 * @private
	 * @type {*}
	 * @memberOf AppDocker
	 */
	private messageErrorTipser: any;

	/**
	 * 往错误信息收集器存信息
	 *
	 * @param {string} type 弹窗类型，success,yellow,error等
	 * @param {*} message 提示内容
	 *
	 * @memberOf AppDocker
	 */
	public pipeMessage(type: string, message: any): void {
		if (this.messageErrorTipser && type !== 'success' && type !== 'info') {
			this.messageErrorTipser.saveMessage(type, message);
		}
	}

	/**
	 * 设置错误到信息收集对象
	 *
	 * @param {*} messager 错误到信息收集对象
	 *
	 * @memberOf AppDocker
	 */
	public setPipeMessager(messager: any): void {
		this.messageErrorTipser = messager;
	}
}
